
import React, { useState, useEffect } from 'react';
import { db } from '../services/db';
import { User, Client, TimeEntry, PayPeriod, PayPeriodStatus } from '../types';
import { Wallet, Clock, CheckCircle2, ArrowRight, Plus, DollarSign, Receipt } from 'lucide-react';

interface DashboardProps {
  user: User;
  onViewClient: (id: string) => void;
  onAddTime: (clientId: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ user, onViewClient, onAddTime }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [periods, setPeriods] = useState<PayPeriod[]>([]);

  useEffect(() => {
    setClients(db.getClients(user.id));
    setEntries(db.getEntries(user.id));
    setPeriods(db.getPayPeriods(user.id));
  }, [user.id]);

  const totalOutstanding = periods
    .filter(p => p.status === PayPeriodStatus.CLOSED || p.status === PayPeriodStatus.SENT)
    .reduce((sum, p) => sum + p.totalAmount, 0);

  const openHours = entries
    .filter(e => !e.payPeriodId)
    .reduce((sum, e) => sum + e.hoursCalculated, 0);

  const openAmount = entries
    .filter(e => !e.payPeriodId)
    .reduce((sum, e) => sum + e.amountCalculated, 0);

  const lifetimePaid = periods
    .filter(p => p.status === PayPeriodStatus.PAID)
    .reduce((sum, p) => sum + p.totalAmount, 0);

  const formatCurrency = (val: number) => `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <header>
        <h1 className="text-2xl font-black text-slate-900 tracking-tight">Dashboard</h1>
        <p className="text-sm text-slate-500 font-medium">Monitoring activity for {user.name}</p>
      </header>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Receipt, label: 'Invoiced / Outstanding', value: formatCurrency(totalOutstanding), color: 'bg-blue-600 text-white shadow-blue-500/20 shadow-lg' },
          { icon: Clock, label: 'Unbilled Hours', value: `${openHours.toFixed(1)}h`, color: 'bg-white text-slate-900' },
          { icon: DollarSign, label: 'Work in Progress', value: formatCurrency(openAmount), color: 'bg-white text-slate-900' },
          { icon: CheckCircle2, label: 'Lifetime Paid', value: formatCurrency(lifetimePaid), color: 'bg-white text-slate-900' },
        ].map((stat, i) => (
          <div key={i} className={`p-5 rounded-xl border border-slate-200 transition-all ${stat.color === 'bg-white text-slate-900' ? 'bg-white' : stat.color}`}>
            <div className="flex justify-between items-start mb-4">
              <span className={`text-[9px] font-black uppercase tracking-widest ${stat.color === 'bg-white text-slate-900' ? 'text-slate-400' : 'text-blue-100 opacity-80'}`}>{stat.label}</span>
              <stat.icon size={14} className={stat.color === 'bg-white text-slate-900' ? 'text-slate-300' : 'text-blue-100'} />
            </div>
            <div className="text-xl font-black tracking-tight">{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Client List */}
      <section className="space-y-4 pt-2">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-black text-slate-800 tracking-tight uppercase tracking-wider">Active Clients</h2>
          {clients.length > 0 && (
            <button onClick={() => onViewClient('new')} className="text-xs font-bold text-blue-600 hover:text-blue-700 transition flex items-center gap-1.5 px-3 py-1 bg-blue-50 rounded-lg">
              <Plus size={12} /> Add Client
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {clients.length === 0 ? (
            <div className="col-span-full py-12 text-center bg-white rounded-xl border-2 border-dashed border-slate-100">
              <p className="text-xs text-slate-400 mb-4 font-bold uppercase tracking-widest">No clients found</p>
              <button 
                onClick={() => onViewClient('new')}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-lg font-bold text-xs hover:bg-blue-700 transition shadow-sm"
              >
                <Plus size={16} /> Add Your First Client
              </button>
            </div>
          ) : (
            clients.map((client) => {
              const clientEntries = entries.filter(e => e.clientId === client.id && !e.payPeriodId);
              const unbilledHrs = clientEntries.reduce((sum, e) => sum + e.hoursCalculated, 0);
              const unbilledAmt = clientEntries.reduce((sum, e) => sum + e.amountCalculated, 0);
              
              return (
                <div key={client.id} className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 flex flex-col justify-between group hover:border-blue-400 transition-all cursor-pointer" onClick={() => onViewClient(client.id)}>
                  <div>
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-black text-base text-slate-800 tracking-tight leading-tight">{client.name}</h3>
                      <ArrowRight size={16} className="text-slate-200 group-hover:text-blue-600 transition-all transform group-hover:translate-x-1" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-end">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Work Progress</span>
                        <span className="font-bold text-slate-800 text-[11px]">{unbilledHrs.toFixed(1)}h logged</span>
                      </div>
                      <div className="w-full bg-slate-50 h-1.5 rounded-full overflow-hidden">
                        <div className="bg-blue-600 h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (unbilledHrs / 40) * 100)}%` }}></div>
                      </div>
                      <div className="text-[10px] font-bold text-blue-600 mt-1">Pending: {formatCurrency(unbilledAmt)}</div>
                    </div>
                  </div>
                  <div className="mt-5 pt-4 border-t border-slate-50 flex gap-2">
                    <button 
                      onClick={(e) => { e.stopPropagation(); onAddTime(client.id); }}
                      className="flex-1 bg-slate-50 hover:bg-blue-50 text-slate-600 hover:text-blue-600 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all border border-transparent hover:border-blue-100"
                    >
                      Log Time
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); onViewClient(client.id); }}
                      className="flex-1 bg-slate-900 hover:bg-black text-white py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all"
                    >
                      History
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
